import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  FiCheckCircle,
  FiChevronDown,
  FiClock,
  FiEdit2,
  FiEye,
  FiPlus,
  FiTrash2,
  FiUploadCloud,
} from "react-icons/fi";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa6";
import { LuCheckCircle, LuClock3, LuHeartHandshake, LuUsers } from "react-icons/lu";
import PayoutDataTable from "./PayoutDataTable";
import PayoutEmptyState from "./PayoutEmptyState";
import PayoutDateRangeFilter from "./PayoutDateRangeFilter";
import PayoutDropdown from "./PayoutDropdown";
import PayoutFilterActions from "./PayoutFilterActions";
import PayoutFilterChips from "./PayoutFilterChips";
import PayoutSearchFilter from "./PayoutSearchFilter";
import PayoutTableCell from "./PayoutTableCell";
import { formatPayoutDateRangeLabel, parsePayoutTableDate } from "./payoutDateUtils";
import { isPayoutApiMode, payoutApi } from "./api/payoutApiClient";
import { beneficiaryFromApiRecord } from "./api/payoutApiAdapters";
import { usePayoutResource } from "./api/usePayoutResource";
import { pickString, readSessionJson } from "./api/payoutMutationHelpers";
import { usePayoutAccess } from "./payoutAccessControl";

type BulkBeneficiaryResult = {
  results?: Record<string, unknown>[];
  data?: {
    results?: Record<string, unknown>[];
  };
};

type BeneficiaryRow = ReturnType<typeof beneficiaryFromApiRecord>;

const beneficiaryStats = [
  {
    label: "Total Beneficiaries",
    value: "0",
    helper: "-",
    helperClass: "text-[#505E8E]",
    icon: <LuUsers />,
    tone: "bg-[#6687FF1A] text-[#6687FF]",
  },
  {
    label: "Verified Beneficiaries",
    value: "0",
    helper: "0.0% of total",
    helperClass: "text-[#505E8E]",
    icon: <LuCheckCircle />,
    tone: "bg-[#16A34A1A] text-[#16A34A]",
  },
  {
    label: "Pending Verification",
    value: "0",
    helper: "0.0% of total",
    helperClass: "text-[#505E8E]",
    icon: <LuClock3 />,
    tone: "bg-[#F59E0B1A] text-[#F97316]",
  },
  {
    label: "Inactive Beneficiaries",
    value: "0",
    helper: "0.0% of total",
    helperClass: "text-[#505E8E]",
    icon: <LuHeartHandshake />,
    tone: "bg-[#EF44441A] text-[#EF4444]",
  },
];

const beneficiaries: BeneficiaryRow[] = [];

const bulkBeneficiaryReviewPath = (beneficiaryId?: string) => {
  const id = String(beneficiaryId || "").trim();
  if (!id) return "";
  const result = readSessionJson<BulkBeneficiaryResult>(
    "payoutBulkBeneficiaryResult",
    {},
  );
  const resultRows = Array.isArray(result.results)
    ? result.results
    : Array.isArray(result.data?.results)
      ? result.data.results
      : [];
  const matched = resultRows.some(
    (row) => pickString(row, ["beneficiary_id", "beneficiaryId", "id"]) === id,
  );
  return matched
    ? `/payouts/beneficiaries/bulk-upload/review?beneficiaryId=${encodeURIComponent(id)}`
    : "";
};

const beneficiaryEditPath = (beneficiary: { id?: string; name: string }) =>
  bulkBeneficiaryReviewPath(beneficiary.id) ||
  `/payouts/beneficiaries/edit/${encodeURIComponent(
    beneficiary.id && String(beneficiary.id).trim()
      ? String(beneficiary.id)
      : beneficiary.name.replace(/\s+/g, "-"),
  )}`;

const categoryClassName: Record<string, string> = {
  Vendor: "bg-[#EDE9FE] text-[#6D28D9]",
  Employee: "bg-[#DBEAFE] text-[#0284C7]",
  Student: "bg-[#DCFCE7] text-[#16A34A]",
  Scholarship: "bg-[#FFEDD5] text-[#F97316]",
};

const statusClassName: Record<string, string> = {
  Verified: "bg-[#DCFCE7] text-[#16A34A]",
  Pending: "bg-[#FEF3C7] text-[#F97316]",
  Inactive: "bg-[#FEE2E2] text-[#EF4444]",
};

function SelectBox({
  label,
  value,
  options,
  onChange,
  className,
}: {
  label: string;
  value: string;
  options?: string[];
  onChange?: (value: string) => void;
  className?: string;
}) {
  return (
    <PayoutDropdown
      label={label}
      value={value}
      options={options}
      onChange={onChange}
      className={className}
    />
  );
}

function PayoutBeneficiaries() {
  const { canAccess } = usePayoutAccess();
  const canManageBeneficiaries = canAccess("beneficiaries.manage");
  const canDeleteBeneficiaries = canAccess("beneficiaries.delete");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchField, setSearchField] = useState("Beneficiary Name");
  const [accountFilter, setAccountFilter] = useState("All Accounts");
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [categoryFilter, setCategoryFilter] = useState("All Categories");
  const [verificationFilter, setVerificationFilter] = useState("All");
  const defaultDateRange = {
    startDate: new Date(2020, 0, 1),
    endDate: new Date(2030, 11, 31, 23, 59, 59),
    key: "selection",
  };
  const [dateRangeFilter, setDateRangeFilter] = useState(defaultDateRange);
  const [pageSize, setPageSize] = useState("10 per page");
  const [currentPage, setCurrentPage] = useState(1);
  const [actionMenuOpen, setActionMenuOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState({
    search: "",
    account: "All Accounts",
    status: "All Status",
    category: "All Categories",
    verification: "All",
    searchField: "Beneficiary Name",
    dateRange: {
      startDate: new Date(2020, 0, 1),
      endDate: new Date(2030, 11, 31, 23, 59, 59),
      key: "selection",
    },
  });
  const rowsPerPage = Number(pageSize.split(" ")[0]) || 10;
  const beneficiaryApiQuery = useMemo(() => {
    const query: Record<string, string | number> = {
      limit: rowsPerPage,
      page: currentPage,
    };
    const search = appliedFilters.search.trim();
    if (search) {
      query.q = search;
      query.search_field = appliedFilters.searchField;
    }
    if (appliedFilters.account !== "All Accounts") {
      query.account = appliedFilters.account;
    }
    if (appliedFilters.status !== "All Status") {
      query.status = appliedFilters.status;
    }
    if (appliedFilters.category !== "All Categories") {
      query.category = appliedFilters.category;
    }
    if (appliedFilters.verification !== "All") {
      query.verification_status = appliedFilters.verification;
    }
    return query;
  }, [appliedFilters, currentPage, rowsPerPage]);
  const beneficiariesResource = usePayoutResource(
    [] as typeof beneficiaries,
    (signal) =>
      payoutApi.beneficiaries(isPayoutApiMode() ? beneficiaryApiQuery : {}, signal).then((rows) =>
        rows.map((row) => beneficiaryFromApiRecord(row, {})),
      ),
    [beneficiaryApiQuery],
  );
  const summaryResource = usePayoutResource(
    null as Record<string, unknown> | null,
    (signal) => payoutApi.beneficiariesSummary(signal),
  );
  const baseBeneficiaries = beneficiariesResource.data;
  const expandedBeneficiaries = useMemo(() => {
    return baseBeneficiaries;
  }, [baseBeneficiaries]);
  const filteredBeneficiaries = useMemo(() => {
    const query = appliedFilters.search.trim().toLowerCase();
    const getBeneficiarySearchValue = (
      beneficiary: (typeof expandedBeneficiaries)[number],
      field: string,
    ) => {
      switch (field) {
        case "Account Number":
          return beneficiary.accountNumber;
        case "IFSC":
          return beneficiary.ifsc;
        case "Bank":
          return beneficiary.bank;
        case "Beneficiary Name":
        default:
          return beneficiary.name;
      }
    };
    return expandedBeneficiaries.filter((beneficiary) => {
      const matchesAccount =
        appliedFilters.account === "All Accounts" ||
        beneficiary.bank.includes(appliedFilters.account.replace(" Account", ""));
      const matchesStatus =
        appliedFilters.status === "All Status" ||
        beneficiary.status === appliedFilters.status;
      const matchesCategory =
        appliedFilters.category === "All Categories" ||
        beneficiary.category === appliedFilters.category;
      const matchesVerification =
        appliedFilters.verification === "All" ||
        beneficiary.status === appliedFilters.verification;
      const lastTransferDate = parsePayoutTableDate(beneficiary.lastTransfer);
      const matchesDate =
        !beneficiary.lastTransfer ||
        beneficiary.lastTransfer === "-" ||
        lastTransferDate === null ||
        (lastTransferDate >= appliedFilters.dateRange.startDate &&
          lastTransferDate <= appliedFilters.dateRange.endDate);
      const matchesSearch =
        !query ||
        getBeneficiarySearchValue(beneficiary, appliedFilters.searchField)
          .toLowerCase()
          .includes(query);

      return (
        matchesAccount &&
        matchesStatus &&
        matchesCategory &&
        matchesVerification &&
        matchesDate &&
        matchesSearch
      );
    });
  }, [appliedFilters, expandedBeneficiaries]);
  const pageCount = Math.max(1, Math.ceil(filteredBeneficiaries.length / rowsPerPage));
  const visibleBeneficiaries = filteredBeneficiaries.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage,
  );
  const resultStart = filteredBeneficiaries.length
    ? (currentPage - 1) * rowsPerPage + 1
    : 0;
  const resultEnd = Math.min(currentPage * rowsPerPage, filteredBeneficiaries.length);
  const beneficiaryPath = (beneficiary: { id?: string; name: string }) =>
    encodeURIComponent(
      beneficiary.id && String(beneficiary.id).trim()
        ? String(beneficiary.id)
        : beneficiary.name.replace(/\s+/g, "-"),
    );
  const beneficiaryTableRows = visibleBeneficiaries.map((beneficiary) => [
    <div className="flex items-center gap-4">
      <span
        className={
          "flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold " +
          beneficiary.avatar
        }
      >
        {beneficiary.initials}
      </span>
      <div className="min-w-0">
        <PayoutTableCell
          value={beneficiary.name}
          valueClassName="font-semibold text-[#1B163B]"
        />
        <p className="mt-1 max-w-[220px] truncate text-[#505E8E]" title={beneficiary.subtitle}>
          {beneficiary.subtitle}
        </p>
      </div>
    </div>,
    <span
      className={
        "rounded-[5px] px-2 py-1 text-xs font-semibold " +
        categoryClassName[beneficiary.category]
      }
    >
      {beneficiary.category}
    </span>,
    <span>
      <PayoutTableCell
        value={beneficiary.bank}
        valueClassName="font-medium text-[#1B163B]"
      />
      <span className="mt-1 block max-w-[220px] truncate text-[#505E8E]" title={beneficiary.accountType}>
        {beneficiary.accountType}
      </span>
    </span>,
    <PayoutTableCell
      value={beneficiary.accountNumber}
      valueClassName="font-semibold text-[#1B163B]"
    />,
    <PayoutTableCell
      value={beneficiary.ifsc}
      valueClassName="font-semibold text-[#1B163B]"
    />,
    <span
      className={
        "inline-flex items-center gap-1 rounded-[5px] px-2 py-1 text-xs font-semibold " +
        statusClassName[beneficiary.status]
      }
    >
      {beneficiary.status === "Verified" && <FiCheckCircle className="text-xs" />}
      {beneficiary.status === "Pending" && <FiClock className="text-xs" />}
      {beneficiary.status}
    </span>,
    <PayoutTableCell
      value={beneficiary.lastTransfer}
      valueClassName="font-semibold text-[#1B163B]"
    />,
    <div className="payout-action-cell">
      <Link
        to={`/payouts/beneficiaries/view/${beneficiaryPath(beneficiary)}`}
        aria-label={`View ${beneficiary.name}`}
        title="View"
        className="payout-action-icon"
      >
        <FiEye className="text-base" />
      </Link>
      {canManageBeneficiaries && (
        <Link
          to={beneficiaryEditPath(beneficiary)}
          aria-label={`Edit ${beneficiary.name}`}
          title="Edit"
          className="payout-action-icon"
        >
          <FiEdit2 className="text-base" />
        </Link>
      )}
      {canDeleteBeneficiaries && (
        <Link
          to={`/payouts/beneficiaries/delete/${beneficiaryPath(beneficiary)}/verify`}
          aria-label={`Delete ${beneficiary.name}`}
          title="Delete"
          className="payout-action-icon payout-action-icon--danger"
        >
          <FiTrash2 className="text-base" />
        </Link>
      )}
    </div>,
  ]);

  useEffect(() => {
    setAppliedFilters({
      search: searchQuery,
      account: accountFilter,
      status: statusFilter,
      category: categoryFilter,
      verification: verificationFilter,
      searchField,
      dateRange: dateRangeFilter,
    });
    setCurrentPage(1);
  }, [
    searchQuery,
    searchField,
    accountFilter,
    statusFilter,
    categoryFilter,
    verificationFilter,
    dateRangeFilter,
  ]);

  const clearFilters = () => {
    setSearchQuery("");
    setSearchField("Beneficiary Name");
    setAccountFilter("All Accounts");
    setStatusFilter("All Status");
    setCategoryFilter("All Categories");
    setVerificationFilter("All");
    setDateRangeFilter(defaultDateRange);
    setCurrentPage(1);
  };

  const exportBeneficiaries = () => {
    const headers = [
      "Beneficiary Name",
      "Category",
      "Bank",
      "Account Number",
      "IFSC",
      "Verification Status",
      "Last Transfer",
    ];
    const csvRows = filteredBeneficiaries.map((beneficiary) =>
      [
        beneficiary.name,
        beneficiary.category,
        beneficiary.bank,
        beneficiary.accountNumber,
        beneficiary.ifsc,
        beneficiary.status,
        beneficiary.lastTransfer,
      ]
        .map((cell) => `"${String(cell).replace(/"/g, '""')}"`)
        .join(","),
    );
    const blob = new Blob([[headers.join(","), ...csvRows].join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "payout-beneficiaries.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="w-full px-4 pt-4 pb-8">
      <div className="mb-7 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-[22px] font-semibold leading-7 text-[#1B163B]">
            Beneficiaries
          </h1>
          <p className="mt-1 text-sm font-medium text-[#505E8E]">
            Manage and view all beneficiaries
          </p>
        </div>
        {canManageBeneficiaries && (
          <div className="relative">
            <button
              type="button"
              onClick={() => setActionMenuOpen((current) => !current)}
              style={{ backgroundColor: "#1B163B", color: "#FFFFFF" }}
              className="flex h-10 items-center gap-3 rounded-[6px] bg-edviron_black px-5 text-sm font-semibold text-white shadow-sm hover:bg-edviron_black/90"
            >
              <FiPlus className="text-lg" />
              Add Beneficiary
              <FiChevronDown
                className={
                  "transition-transform " + (actionMenuOpen ? "rotate-180" : "")
                }
              />
            </button>
            {actionMenuOpen && (
              <div className="absolute right-0 z-30 mt-2 w-[230px] overflow-hidden rounded-[8px] border border-[#E0E5F2] bg-white new-shadow">
                <Link
                  to="/payouts/beneficiaries/add"
                  onClick={() => setActionMenuOpen(false)}
                  className="flex w-full items-center gap-3 px-4 py-3 text-sm font-semibold text-[#1B163B] hover:bg-[#A9B2CF1A]"
                >
                  <FiPlus className="text-[#6687FF]" />
                  Single Beneficiary
                </Link>
                <Link
                  to="/payouts/beneficiaries/bulk-upload"
                  onClick={() => setActionMenuOpen(false)}
                  className="flex w-full items-center gap-3 px-4 py-3 text-sm font-semibold text-[#1B163B] hover:bg-[#A9B2CF1A]"
                >
                  <FiUploadCloud className="text-[#6687FF]" />
                  Bulk Upload
                </Link>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="mb-8 grid gap-5 lg:grid-cols-4">
        {(() => {
              const summary = summaryResource.data || {};
              const total =
                Number(summary.total ?? baseBeneficiaries.length) ||
                baseBeneficiaries.length;
              const verified = Number(summary.verified ?? 0) || 0;
              const pending = Number(summary.pending ?? 0) || 0;
              const inactive = Number(summary.inactive ?? 0) || 0;
              return [
                {
                  ...beneficiaryStats[0],
                  value: String(total),
                  helper: "From linked school",
                  helperClass: "text-[#505E8E]",
                },
                {
                  ...beneficiaryStats[1],
                  value: String(verified),
                  helper: total ? `${Math.round((verified / total) * 100)}% of total` : "0% of total",
                },
                {
                  ...beneficiaryStats[2],
                  value: String(pending),
                  helper: total ? `${Math.round((pending / total) * 100)}% of total` : "0% of total",
                },
                {
                  ...beneficiaryStats[3],
                  value: String(inactive),
                  helper: total ? `${Math.round((inactive / total) * 100)}% of total` : "0% of total",
                },
              ];
            })().map((stat) => (
          <div
            key={stat.label}
            className="flex min-h-[118px] items-center gap-5 rounded-[8px] border border-[#E0E5F2] bg-white px-6 py-5 new-shadow"
          >
            <div
              className={
                "payout-kpi-icon flex h-11 w-11 shrink-0 items-center justify-center text-xl " +
                stat.tone
              }
            >
              {stat.icon}
            </div>
            <div>
              <p className="text-xs font-semibold text-[#505E8E]">
                {stat.label}
              </p>
              <p className="mt-2 text-[22px] font-semibold leading-7 text-[#1B163B]">
                {stat.value}
              </p>
              <p className={"mt-4 text-xs font-semibold " + stat.helperClass}>
                {stat.helper}
              </p>
            </div>
          </div>
        ))}
      </div>

      <section className="payout-filter-card payout-filter-grid payout-filter-grid--with-search mb-5 rounded-[8px] border border-[#E0E5F2] bg-white px-5 py-5 new-shadow">
        <PayoutSearchFilter
          value={searchQuery}
          onChange={setSearchQuery}
          filterBy={searchField}
          onFilterByChange={setSearchField}
          filterOptions={[
            "Beneficiary Name",
            "Account Number",
            "IFSC",
            "Bank",
          ]}
          placeholder={`Search by ${searchField}`}
          className="w-full lg:col-span-2"
        />
        <PayoutDateRangeFilter className="w-full min-w-0" onChange={setDateRangeFilter} />
        <SelectBox
          label="Account"
          value={accountFilter}
          options={["All Accounts", "Operating Account", "Salary Account", "Scholarship Account", "Vendor Account"]}
          onChange={setAccountFilter}
          className="w-full min-w-0"
        />
        <SelectBox
          label="Status"
          value={statusFilter}
          options={["All Status", "Verified", "Pending", "Inactive"]}
          onChange={setStatusFilter}
          className="w-full min-w-0"
        />
        <SelectBox
          label="Category"
          value={categoryFilter}
          options={["All Categories", "Vendor", "Employee", "Student", "Scholarship"]}
          onChange={setCategoryFilter}
          className="w-full min-w-0"
        />
        <SelectBox
          label="Verification"
          value={verificationFilter}
          options={["All", "Verified", "Pending", "Inactive"]}
          onChange={setVerificationFilter}
          className="w-full min-w-0"
        />
        <PayoutFilterActions
          onClear={clearFilters}
          onExport={exportBeneficiaries}
          className="w-full"
        />
        <PayoutFilterChips
          className="col-span-full"
          onClearAll={clearFilters}
          chips={[
            {
              label: "Date Range",
              value: formatPayoutDateRangeLabel(appliedFilters.dateRange),
              hidden:
                appliedFilters.dateRange.startDate.getTime() ===
                  defaultDateRange.startDate.getTime() &&
                appliedFilters.dateRange.endDate.getTime() ===
                  defaultDateRange.endDate.getTime(),
              onRemove: () => setDateRangeFilter(defaultDateRange),
            },
            {
              label: "Account",
              value: appliedFilters.account,
              onRemove: () => setAccountFilter("All Accounts"),
            },
            {
              label: "Status",
              value: appliedFilters.status,
              onRemove: () => setStatusFilter("All Status"),
            },
            {
              label: "Category",
              value: appliedFilters.category,
              onRemove: () => setCategoryFilter("All Categories"),
            },
            {
              label: "Verification",
              value: appliedFilters.verification,
              onRemove: () => setVerificationFilter("All"),
            },
            {
              label: "Search",
              value: appliedFilters.search,
              onRemove: () => setSearchQuery(""),
            },
          ]}
        />
      </section>

      <PayoutDataTable
        columns={[
          "Beneficiary Name",
          "Category",
          "Bank Details",
          "Account Number",
          "IFSC",
          "Verification Status",
          "Last Transfer",
          "Actions",
        ]}
        rows={beneficiaryTableRows}
        minWidth="min-w-[1160px]"
        emptyState={
          <PayoutEmptyState
            icon={<LuHeartHandshake />}
            title="No beneficiaries found"
            description="Add beneficiaries before initiating payouts. Verified bank details will appear here for faster transfers."
            action={
              canManageBeneficiaries ? (
                <Link
                  to="/payouts/beneficiaries/add"
                  className="flex h-10 items-center gap-2 rounded-[6px] bg-[#1B163B] px-5 text-sm font-semibold text-white"
                >
                  <FiPlus /> Add Beneficiary
                </Link>
              ) : null
            }
            secondaryAction={
              canManageBeneficiaries ? (
                <Link
                  to="/payouts/beneficiaries/bulk-upload"
                  className="flex h-10 items-center gap-2 rounded-[6px] border border-[#E0E5F2] bg-white px-5 text-sm font-semibold text-[#1B163B]"
                >
                  <FiUploadCloud /> Bulk Upload
                </Link>
              ) : null
            }
          />
        }
        footer={
          <div className="flex min-h-[64px] w-full items-center justify-between gap-4 bg-white px-5 py-4 text-xs font-medium text-[#505E8E]">
            <span className="min-w-max">
              Showing {resultStart} to {resultEnd} of {filteredBeneficiaries.length} results
            </span>
            <div className="ml-auto flex min-w-max items-center gap-4">
              <PayoutDropdown
                value={pageSize}
                options={["10 per page", "25 per page", "50 per page"]}
                onChange={(value) => {
                  setPageSize(value);
                  setCurrentPage(1);
                }}
                className="min-w-[120px]"
              />
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                  className="flex h-9 w-9 items-center justify-center rounded-md border border-[#E0E5F2] text-[#505E8E] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <FaArrowLeft className="text-xs" />
                </button>
                {[1, 2, 3].filter((page) => page <= pageCount).map((page) => (
                  <button
                    type="button"
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={
                      "h-9 w-9 rounded-md " +
                      (currentPage === page
                        ? "border border-[#6687FF] bg-[#6687FF1A] text-[#6687FF]"
                        : "border border-[#E0E5F2] text-[#1B163B]")
                    }
                  >
                    {page}
                  </button>
                ))}
                {pageCount > 3 && (
                  <>
                    <span className="px-2 text-[#505E8E]">...</span>
                    <button
                      type="button"
                      onClick={() => setCurrentPage(pageCount)}
                      className={
                        "h-9 w-9 rounded-md " +
                        (currentPage === pageCount
                          ? "border border-[#6687FF] bg-[#6687FF1A] text-[#6687FF]"
                          : "border border-[#E0E5F2] text-[#1B163B]")
                      }
                    >
                      {pageCount}
                    </button>
                  </>
                )}
                <button
                  type="button"
                  disabled={currentPage === pageCount}
                  onClick={() => setCurrentPage((page) => Math.min(pageCount, page + 1))}
                  className="flex h-9 w-9 items-center justify-center rounded-md border border-[#E0E5F2] text-[#505E8E] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <FaArrowRight className="text-xs" />
                </button>
              </div>
            </div>
          </div>
        }
      />

      <section className="hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1160px] table-auto">
            <thead className="bg-[#A9B2CF1A] text-center">
              <tr className="text-left text-[11px] font-semibold text-[#505E8E]">
                <th className="px-5 py-4 text-center">Beneficiary Name</th>
                <th className="px-5 py-4 text-center">Category</th>
                <th className="px-5 py-4 text-center">Bank Details</th>
                <th className="px-5 py-4 text-center">Account Number</th>
                <th className="px-5 py-4 text-center">IFSC</th>
                <th className="px-5 py-4 text-center">Verification Status</th>
                <th className="px-5 py-4 text-center">Last Transfer</th>
                <th className="px-5 py-4 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleBeneficiaries.map((beneficiary) => (
                <tr
                  key={`${beneficiary.name}-${beneficiary.accountNumber}`}
                  className="border-t border-[#E0E5F2] text-xs font-medium text-[#1B163B] hover:bg-[#A9B2CF1A]"
                >
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-4">
                      <span
                        className={
                          "flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold " +
                          beneficiary.avatar
                        }
                      >
                        {beneficiary.initials}
                      </span>
                      <div>
                        <p className="font-semibold">{beneficiary.name}</p>
                        <p className="mt-1 text-[#505E8E]">
                          {beneficiary.subtitle}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <span
                      className={
                        "rounded-[5px] px-2 py-1 text-xs font-semibold " +
                        categoryClassName[beneficiary.category]
                      }
                    >
                      {beneficiary.category}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <p>{beneficiary.bank}</p>
                    <p className="mt-1 text-[#505E8E]">
                      {beneficiary.accountType}
                    </p>
                  </td>
                  <td className="px-5 py-4 font-semibold">
                    {beneficiary.accountNumber}
                  </td>
                  <td className="px-5 py-4 font-semibold">{beneficiary.ifsc}</td>
                  <td className="px-5 py-4">
                    <span
                      className={
                        "inline-flex items-center gap-1 rounded-[5px] px-2 py-1 text-xs font-semibold " +
                        statusClassName[beneficiary.status]
                      }
                    >
                      {beneficiary.status === "Verified" && (
                        <FiCheckCircle className="text-xs" />
                      )}
                      {beneficiary.status === "Pending" && (
                        <FiClock className="text-xs" />
                      )}
                      {beneficiary.status}
                    </span>
                  </td>
                  <td className="px-5 py-4 font-semibold">
                    {beneficiary.lastTransfer}
                  </td>
                  <td className="px-5 py-4">
                    <div className="payout-action-cell">
                      <Link
                        to={`/payouts/beneficiaries/view/${beneficiaryPath(beneficiary)}`}
                        aria-label={`View ${beneficiary.name}`}
                        title="View"
                        className="payout-action-icon"
                      >
                        <FiEye className="text-base" />
                      </Link>
                      {canManageBeneficiaries && (
                        <Link
                          to={beneficiaryEditPath(beneficiary)}
                          aria-label={`Edit ${beneficiary.name}`}
                          title="Edit"
                          className="payout-action-icon"
                        >
                          <FiEdit2 className="text-base" />
                        </Link>
                      )}
                      {canDeleteBeneficiaries && (
                        <Link
                          to={`/payouts/beneficiaries/delete/${beneficiaryPath(beneficiary)}/verify`}
                          aria-label={`Delete ${beneficiary.name}`}
                          title="Delete"
                          className="payout-action-icon payout-action-icon--danger"
                        >
                          <FiTrash2 className="text-base" />
                        </Link>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex min-h-[64px] w-full items-center justify-between gap-4 border-t border-[#E0E5F2] px-5 py-4 text-xs font-medium text-[#505E8E]">
          <span className="min-w-max">
            Showing {resultStart} to {resultEnd} of {filteredBeneficiaries.length} results
          </span>
          <div className="ml-auto flex min-w-max items-center gap-4">
            <PayoutDropdown
              value={pageSize}
              options={["10 per page", "25 per page", "50 per page"]}
              onChange={(value) => {
                setPageSize(value);
                setCurrentPage(1);
              }}
              className="min-w-[120px]"
            />
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                className="flex h-9 w-9 items-center justify-center rounded-md border border-[#E0E5F2] text-[#505E8E] disabled:cursor-not-allowed disabled:opacity-50"
              >
                <FaArrowLeft className="text-xs" />
              </button>
              {[1, 2, 3].filter((page) => page <= pageCount).map((page) => (
                <button
                  type="button"
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={
                    "h-9 w-9 rounded-md " +
                    (currentPage === page
                      ? "border border-[#6687FF] bg-[#6687FF1A] text-[#6687FF]"
                      : "border border-[#E0E5F2] text-[#1B163B]")
                  }
                >
                  {page}
                </button>
              ))}
              {pageCount > 3 && (
                <>
                  <span className="px-2 text-[#505E8E]">...</span>
                  <button
                    type="button"
                    onClick={() => setCurrentPage(pageCount)}
                    className={
                      "h-9 w-9 rounded-md " +
                      (currentPage === pageCount
                        ? "border border-[#6687FF] bg-[#6687FF1A] text-[#6687FF]"
                        : "border border-[#E0E5F2] text-[#1B163B]")
                    }
                  >
                    {pageCount}
                  </button>
                </>
              )}
              <button
                type="button"
                disabled={currentPage === pageCount}
                onClick={() => setCurrentPage((page) => Math.min(pageCount, page + 1))}
                className="flex h-9 w-9 items-center justify-center rounded-md border border-[#E0E5F2] text-[#505E8E] disabled:cursor-not-allowed disabled:opacity-50"
              >
                <FaArrowRight className="text-xs" />
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default PayoutBeneficiaries;
